import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Grid, Tooltip } from '@mui/material';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import EquiposClub from '../components/clubes/EquiposClub';
import { BoxTitleApp, ContainerApp, PaperApp } from '../components/custom/CustomComponents';
import CrearJugadorModal from '../components/jugador/CrearJugadorModal';
import ListasUsuario from '../components/listas/ListasUsuario';
import InformesUsuario from '../components/usuario/InformesUsuario';
import JugadoresManualesUsuario from '../components/usuario/JugadoresManualesUsuario';
import ObservacionesUsuario from '../components/usuario/ObservacionesUsuario';
import { Context } from '../service/Context';
import { ordenarPorCategoria } from '../utils/UtilesComun';

const Home = () => {
  const modalCrearJugadorRef = useRef(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const isScouting = location.pathname === '/scouting' || tabParam === 'scouting';

  const {
    obtenerDatosHome,
    idUsuario,
    obtenerJugadoresManuales,
    obtenerInformesUsuario,
    datosHomeCache,
    setDatosHomeCache,
    jugadoresManualesCache,
    setJugadoresManualesCache,
    informesUsuarioCache,
    setInformesUsuarioCache,
  } = useContext(Context);
  const [data, setData] = useState(datosHomeCache);
  const [jugadoresManuales, setJugadoresManuales] = useState(jugadoresManualesCache || []);
  const [informesUsuario, setInformesUsuario] = useState(informesUsuarioCache || []);
  const [loadingHome, setLoadingHome] = useState(!datosHomeCache);
  const [loadingManuales, setLoadingManuales] = useState(!jugadoresManualesCache);
  const [loadingInformes, setLoadingInformes] = useState(!informesUsuarioCache);

  const fetchData = useCallback(
    async (force = false) => {
      // 1. Carga independiente de datos de Home (Mi Club, Observaciones, Listas)
      obtenerDatosHome(idUsuario, force)
        .then((result) => {
          if (result) {
            setData(result);
            setDatosHomeCache(result);
          }
          setLoadingHome(false);
        })
        .catch((error) => {
          console.error('Error al cargar datos en Home:', error);
          setLoadingHome(false);
        });

      // 2. Carga independiente de Jugadores Manuales
      obtenerJugadoresManuales(idUsuario, force)
        .then((manuales) => {
          if (manuales) {
            setJugadoresManuales(manuales);
            setJugadoresManualesCache(manuales);
          }
          setLoadingManuales(false);
        })
        .catch((error) => {
          console.error('Error al cargar jugadores manuales:', error);
          setLoadingManuales(false);
        });

      // 3. Carga independiente de Informes
      obtenerInformesUsuario(idUsuario, force)
        .then((informes) => {
          if (informes) {
            setInformesUsuario(informes);
            setInformesUsuarioCache(informes);
          }
          setLoadingInformes(false);
        })
        .catch((error) => {
          console.error('Error al cargar informes de usuario:', error);
          setLoadingInformes(false);
        });
    },
    [
      idUsuario,
      obtenerDatosHome,
      obtenerJugadoresManuales,
      obtenerInformesUsuario,
      setDatosHomeCache,
      setJugadoresManualesCache,
      setInformesUsuarioCache,
    ]
  );

  useEffect(() => {
    let isMounted = true;
    const doFetch = async () => {
      const forceRefresh = Boolean(datosHomeCache);
      if (isMounted) {
        await fetchData(forceRefresh);
      }
    };

    doFetch();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  // Inicializar o corregir en almacenamiento el último equipo visitado del club propio
  useEffect(() => {
    if (data?.equiposClub?.length > 0) {
      const stored = localStorage.getItem('ultimoEquipoClub');
      const esValido = stored && data.equiposClub.some((eq) => String(eq.idEquipo) === String(stored));
      if (!esValido) {
        const ordenados = ordenarPorCategoria(data.equiposClub);
        if (ordenados.length > 0 && ordenados[0]?.idEquipo) {
          localStorage.setItem('ultimoEquipoClub', String(ordenados[0].idEquipo));
        }
      }
    }
  }, [data?.equiposClub]);

  const handleObservacionUpdate = (idJugador, updatedObservaciones) => {
    setData((prevData) => {
      if (!prevData) {
        return prevData;
      }
      const otherObservaciones = (prevData.observaciones || []).filter((obs) => obs.idJugador !== idJugador);
      const filteredUpdatedObservaciones = updatedObservaciones.filter((obs) => obs.idUsuario === idUsuario);
      const newObservaciones = [...otherObservaciones, ...filteredUpdatedObservaciones];
      const updated = { ...prevData, observaciones: newObservaciones };
      setDatosHomeCache(updated);
      return updated;
    });
  };

  return (
    <>
      <ContainerApp maxWidth={false} sx={{ mt: 2 }}>
        {/* Panel de control: Equipos del Club */}
        {!isScouting && (
          <PaperApp>
            <BoxTitleApp sx={{ mt: -0.5, mb: 1 }}>Mi Club</BoxTitleApp>
            {loadingHome && (!data?.equiposClub || data.equiposClub.length === 0) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180, py: 4 }}>
                <CircularProgress size={30} sx={{ color: 'primary.main' }} />
              </Box>
            ) : (
              <EquiposClub equiposClub={data?.equiposClub || []} />
            )}
          </PaperApp>
        )}

        {/* Scouting (2 Columnas x 2 Filas: Izquierda: Informes + Observaciones | Derecha: Listas + Jugadores manuales) */}
        {isScouting && (
          <Grid container spacing={2.5}>
            {/* Columna Izquierda: Mis Informes (arriba) y Mis Observaciones (debajo) */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* 1. Mis Informes */}
                <PaperApp>
                  {loadingInformes && (!informesUsuario || informesUsuario.length === 0) ? (
                    <>
                      <BoxTitleApp sx={{ mt: -0.5, mb: 1 }}>Mis Informes</BoxTitleApp>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 180,
                          py: 4,
                        }}
                      >
                        <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                      </Box>
                    </>
                  ) : (
                    <InformesUsuario
                      informes={informesUsuario || []}
                      onUpdate={() => fetchData(true)}
                      equiposClub={data?.equiposClub || []}
                    />
                  )}
                </PaperApp>

                {/* 2. Mis Observaciones */}
                <PaperApp>
                  {loadingHome && (!data?.observaciones || data.observaciones.length === 0) ? (
                    <>
                      <BoxTitleApp sx={{ mt: -0.5, mb: 1 }}>Mis Observaciones</BoxTitleApp>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 180,
                          py: 4,
                        }}
                      >
                        <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                      </Box>
                    </>
                  ) : (
                    <ObservacionesUsuario
                      observaciones={data?.observaciones || []}
                      onUpdate={handleObservacionUpdate}
                      equiposClub={data?.equiposClub || []}
                      compact
                    />
                  )}
                </PaperApp>
              </Box>
            </Grid>

            {/* Columna Derecha: Mis Listas (arriba) y Jugadores creados manualmente (debajo) */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* 1. Listas */}
                <PaperApp>
                  {loadingHome && !data?.misListas && !data?.otrasListas ? (
                    <>
                      <BoxTitleApp sx={{ mt: -0.5, mb: 1 }}>Mis Listas</BoxTitleApp>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 180,
                          py: 4,
                        }}
                      >
                        <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                      </Box>
                    </>
                  ) : (
                    <ListasUsuario
                      todasListas={[...(data?.misListas || []), ...(data?.otrasListas || [])]}
                      onUpdate={() => fetchData(true)}
                    />
                  )}
                </PaperApp>

                {/* 2. Jugadores creados manualmente */}
                <PaperApp>
                  <BoxTitleApp
                    action={
                      <Tooltip title="Crear jugador" arrow placement="top">
                        <Button
                          size="small"
                          onClick={() => modalCrearJugadorRef.current?.handleOpen()}
                          startIcon={<AddIcon sx={{ fontSize: '0.95rem !important' }} />}
                          sx={{
                            height: 26,
                            px: 1.25,
                            borderRadius: '13px',
                            backgroundColor: '#0f766e',
                            color: '#ffffff',
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)',
                            '&:hover': {
                              backgroundColor: '#0d9488',
                              boxShadow: '0 2px 5px rgba(15, 118, 110, 0.3)',
                            },
                          }}
                        >
                          Crear
                        </Button>
                      </Tooltip>
                    }
                  >
                    Jugadores Creados Manualmente
                  </BoxTitleApp>
                  {loadingManuales && (!jugadoresManuales || jugadoresManuales.length === 0) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 180,
                        py: 4,
                      }}
                    >
                      <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                    </Box>
                  ) : (
                    <JugadoresManualesUsuario jugadores={jugadoresManuales || []} idUsuario={idUsuario} compact />
                  )}
                </PaperApp>
              </Box>
            </Grid>
          </Grid>
        )}
      </ContainerApp>
      <CrearJugadorModal ref={modalCrearJugadorRef} onUpdate={() => fetchData(true)} />
    </>
  );
};

export default Home;
